/**
 * ResearchNoteService
 *
 * Manages genealogy research notes with tasks, source citations, and collaboration.
 * Extends BaseContentService for standardized 10-step integration pattern.
 *
 * Features:
 * - Research objective tracking
 * - Task management for research activities
 * - Source citation management
 * - Progress tracking and completion percentage
 * - Collaborative research (private/shared notes)
 * - Assignment to researchers
 * - Approval workflow for shared notes
 * - SystemConfig validation
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  ResearchNote,
  ResearchType,
  ResearchStatus,
  Priority,
  ResearchTask,
  SourceCitation,
  ResearchNoteFormData,
  ResearchNoteFilters,
  ResearchNoteApprovalStatus,
} from '../types/researchNote';
import { SystemConfig } from '../types';

import {
  BaseContentService,
  BaseContentEntity,
  CreateContentOptions,
} from './baseContentService';
import { SystemConfigService } from './systemConfigService';
import { ChangeTrackingService } from './changeTrackingService';
import { AuditLogService } from './auditLogService';
import { NotificationService } from './notificationService';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * ResearchNote entity extending base content entity
 */
export interface ResearchNoteEntity extends BaseContentEntity {
  // Association
  personName: string;

  // Content
  title: string;
  objective: string;
  findings?: string;
  hypothesis?: string;

  // Classification
  type: ResearchType;
  status: ResearchStatus;
  priority: Priority;

  // Tasks
  tasks: ResearchTask[];

  // Sources
  sources: SourceCitation[];

  // Documents
  linkedDocumentIds: string[];

  // Collaboration
  isPrivate: boolean;
  assignedTo: string[];
  assignedToNames: string[];

  // Metadata
  researcherId: string;
  researcherName: string;
  lastUpdatedBy: string;
  lastUpdatedByName: string;

  // Progress tracking
  completionPercentage: number;
  lastActivityAt: Date;

  // Moderation (only for shared notes)
  approvalStatus?: ResearchNoteApprovalStatus;
  approvedBy?: string;
  approvedAt?: Date;
  rejectionReason?: string;

  // Soft delete
  deletedBy?: string;
  deletedAt?: Date;
}

/**
 * ResearchNote creation data
 */
interface ResearchNoteCreationData {
  personId: string;
  personName: string;
  title: string;
  objective: string;
  findings?: string;
  hypothesis?: string;
  type: ResearchType;
  status: ResearchStatus;
  priority: Priority;
  isPrivate: boolean;
  assignedTo: string[];
  assignedToNames: string[];
  researcherName: string;
}

// ============================================================================
// ResearchNoteService Class
// ============================================================================

/**
 * ResearchNoteService - Manages research note operations
 * Extends BaseContentService for 10-step compliance
 */
class ResearchNoteServiceImpl extends BaseContentService<ResearchNoteEntity> {
  // ============================================================================
  // ABSTRACT PROPERTIES IMPLEMENTATION
  // ============================================================================

  protected collectionName = 'researchNotes';
  protected featureFlagKey: keyof SystemConfig['features'] = 'enableResearchTab';
  protected approvalConfigKey: keyof SystemConfig['content'] = 'requireResearchNoteApproval';
  protected maxPerPersonConfigKey: keyof SystemConfig['content'] = 'maxResearchNotesPerPerson';
  protected authorFieldName = 'researcherId'; // Research notes use researcherId as author field
  protected changeTrackingEntityType = 'research';

  // ============================================================================
  // ABSTRACT METHODS IMPLEMENTATION
  // ============================================================================

  /**
   * Validate research note-specific data
   */
  protected async validateContent(data: ResearchNoteCreationData, config: SystemConfig): Promise<void> {
    // Check if research notes are enabled
    if (!config.content.enableResearchNotes) {
      throw new Error('Research notes are currently disabled');
    }

    // Validate required fields
    if (!data.title || data.title.trim().length === 0) {
      throw new Error('Research note title is required');
    }

    if (!data.objective || data.objective.trim().length === 0) {
      throw new Error('Research objective is required');
    }

    if (!data.personId) {
      throw new Error('personId is required for research note creation');
    }
  }

  /**
   * Build Firestore query constraints from filters
   */
  protected buildFilters(filters: ResearchNoteFilters): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];

    if (filters.type) {
      constraints.push(where('type', '==', filters.type));
    }

    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }

    if (filters.priority) {
      constraints.push(where('priority', '==', filters.priority));
    }

    if (filters.isPrivate !== undefined) {
      constraints.push(where('isPrivate', '==', filters.isPrivate));
    }

    if (filters.researcherId) {
      constraints.push(where('researcherId', '==', filters.researcherId));
    }

    if (filters.approvalStatus) {
      constraints.push(where('approvalStatus', '==', filters.approvalStatus));
    }

    return constraints;
  }

  /**
   * Map Firestore document data to typed entity
   */
  protected mapDocumentToEntity(docData: any, docId: string): ResearchNoteEntity {
    return {
      id: docId,
      personId: docData.personId,
      personName: docData.personName,
      title: docData.title,
      objective: docData.objective,
      findings: docData.findings,
      hypothesis: docData.hypothesis,
      type: docData.type,
      status: docData.status,
      priority: docData.priority,
      tasks: docData.tasks || [],
      sources: docData.sources || [],
      linkedDocumentIds: docData.linkedDocumentIds || [],
      isPrivate: docData.isPrivate,
      assignedTo: docData.assignedTo || [],
      assignedToNames: docData.assignedToNames || [],
      researcherId: docData.researcherId,
      researcherName: docData.researcherName,
      lastUpdatedBy: docData.lastUpdatedBy,
      lastUpdatedByName: docData.lastUpdatedByName,
      completionPercentage: docData.completionPercentage || 0,
      lastActivityAt: this.timestampToDate(docData.lastActivityAt),
      approvalStatus: docData.approvalStatus,
      approvedBy: docData.approvedBy,
      approvedAt: docData.approvedAt ? this.timestampToDate(docData.approvedAt) : undefined,
      rejectionReason: docData.rejectionReason,
      createdAt: this.timestampToDate(docData.createdAt),
      updatedAt: this.timestampToDate(docData.updatedAt),
      createdBy: docData.createdBy || docData.researcherId, // Fallback for old data
      isApproved: docData.isApproved,
      isDeleted: docData.isDeleted,
      deletedBy: docData.deletedBy,
      deletedAt: docData.deletedAt ? this.timestampToDate(docData.deletedAt) : undefined,
    };
  }

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Create a new research note
   * Follows complete 10-step integration pattern
   */
  async createResearchNote(
    formData: ResearchNoteFormData,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<ResearchNote> {
    try {
      // Prepare research note data
      const now = new Date();
      const researchNoteData: ResearchNoteCreationData = {
        personId: formData.personId,
        personName: '', // Will be populated by UI
        title: formData.title,
        objective: formData.objective,
        findings: formData.findings,
        hypothesis: formData.hypothesis,
        type: formData.type,
        status: formData.status,
        priority: formData.priority,
        isPrivate: formData.isPrivate,
        assignedTo: formData.assignedTo || [],
        assignedToNames: [], // Will be populated by UI
        researcherName: userName,
      };

      // Execute 10-step pattern via BaseContentService
      // Private research notes skip approval; shared notes go through approval workflow
      const createdEntity = await this.executeCreatePattern(
        researchNoteData,
        userId,
        userName,
        userRole,
        {
          requiresApproval: !formData.isPrivate, // Private notes don't need approval
          entityTypeName: 'research note',
          skipNotification: formData.isPrivate, // Don't notify for private notes
        }
      );

      // Add additional fields not in base entity
      const researchNote: ResearchNote = {
        ...createdEntity,
        tasks: [],
        sources: [],
        linkedDocumentIds: [],
        lastUpdatedBy: userId,
        lastUpdatedByName: userName,
        completionPercentage: 0,
        lastActivityAt: now,
        approvalStatus: createdEntity.isPrivate
          ? undefined
          : (createdEntity.isApproved ? 'approved' : 'pending'),
      } as unknown as ResearchNote;

      // Send assignment notifications (non-blocking)
      if (formData.assignedTo && formData.assignedTo.length > 0) {
        formData.assignedTo.forEach((assignedUserId) => {
          if (assignedUserId !== userId) {
            NotificationService.createNotification({
              userId: assignedUserId,
              type: 'research_note_assigned',
              actionUrl: `/research/${createdEntity.id}`,
              data: {
                researchNoteId: createdEntity.id,
                title: formData.title,
                assignedBy: userName,
              },
            }).catch(err => console.error('Failed to create research assignment notification:', err));
          }
        });
      }

      return researchNote;

    } catch (error: any) {
      console.error('Error creating research note:', error);
      throw new Error(`Failed to create research note: ${error.message}`);
    }
  }

  /**
   * Get all research notes for a specific person
   */
  async getResearchNotesByPerson(
    personId: string,
    filters?: ResearchNoteFilters,
    userId?: string
  ): Promise<ResearchNote[]> {
    try {
      const entities = await this.getByPerson(personId, filters, userId);

      // Filter out private notes unless they belong to the current user
      // Filter out pending shared notes unless user is admin or author
      const filteredEntities = entities.filter((entity) => {
        if (entity.isPrivate && entity.researcherId !== userId) {
          return false;
        }

        if (
          !entity.isPrivate &&
          entity.approvalStatus === 'pending' &&
          entity.researcherId !== userId
        ) {
          return false;
        }

        return true;
      });

      return filteredEntities as unknown as ResearchNote[];
    } catch (error: any) {
      console.error('Error getting research notes:', error);
      throw new Error(`Failed to get research notes: ${error.message}`);
    }
  }

  /**
   * Get a specific research note by ID
   */
  async getResearchNoteById(noteId: string, userId?: string): Promise<ResearchNote | null> {
    try {
      const entity = await this.getById(noteId, userId);
      if (!entity) {
        return null;
      }

      // Check if user has permission to view private research note
      if (entity.isPrivate && entity.researcherId !== userId) {
        return null;
      }

      return entity as unknown as ResearchNote;
    } catch (error: any) {
      console.error('Error getting research note:', error);
      throw new Error(`Failed to get research note: ${error.message}`);
    }
  }

  /**
   * Update a research note
   */
  async updateResearchNote(
    noteId: string,
    updates: Partial<ResearchNoteFormData>,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Get existing note
      const docRef = doc(db, this.collectionName, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Research note not found');
      }

      const existingNote = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingNote, userId, userRole)) {
        throw new Error('You do not have permission to update this research note');
      }

      // Prepare update data
      const updateData = {
        ...updates,
        lastUpdatedBy: userId,
        lastUpdatedByName: userName,
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Update in Firestore
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, this.removeUndefinedFields(updateData));

      // Get updated note for change tracking
      const updatedSnap = await getDoc(docRef);
      const updatedNote = this.mapDocumentToEntity(updatedSnap.data()!, updatedSnap.id);

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'research',
          entityId: noteId,
          changeType: 'update',
          beforeState: existingNote,
          afterState: updatedNote,
        },
        userId,
        userName,
        true
      );

      // Log audit
      await AuditLogService.logAction({
        userId,
        action: 'content_approved',
        contentType: 'research-note',
        contentId: noteId,
        details: {
          operation: 'update',
          userName,
          personId: existingNote.personId,
          updates,
          isPrivate: existingNote.isPrivate,
        },
      });

    } catch (error: any) {
      console.error('Error updating research note:', error);
      throw new Error(`Failed to update research note: ${error.message}`);
    }
  }

  /**
   * Add a task to a research note
   */
  async addTask(
    noteId: string,
    task: Omit<ResearchTask, 'id' | 'createdAt'>,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Research note not found');
      }

      const existingNote = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingNote, userId, userRole)) {
        throw new Error('You do not have permission to add tasks to this research note');
      }

      // Create new task
      const newTask: ResearchTask = {
        ...task,
        id: `task_${Date.now()}_${Math.random().toString(36).substring(2)}`,
      };

      const updatedTasks = [...existingNote.tasks, newTask];

      // Update document
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        tasks: updatedTasks,
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'research',
          entityId: noteId,
          changeType: 'update',
          beforeState: existingNote,
          afterState: { ...existingNote, tasks: updatedTasks },
        },
        userId,
        userName,
        true
      );

    } catch (error: any) {
      console.error('Error adding task:', error);
      throw new Error(`Failed to add task: ${error.message}`);
    }
  }

  /**
   * Update task status in a research note
   */
  async updateTaskStatus(
    noteId: string,
    taskId: string,
    completed: boolean,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Research note not found');
      }

      const existingNote = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingNote, userId, userRole)) {
        throw new Error('You do not have permission to update tasks in this research note');
      }

      // Update task status
      const updatedTasks = existingNote.tasks.map(task =>
        task.id === taskId ? { ...task, isCompleted: completed } : task
      );

      // Calculate completion percentage
      const completedTasks = updatedTasks.filter(t => t.isCompleted).length;
      const completionPercentage = updatedTasks.length > 0
        ? Math.round((completedTasks / updatedTasks.length) * 100)
        : 0;

      // Update document
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        tasks: updatedTasks,
        completionPercentage,
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'research',
          entityId: noteId,
          changeType: 'update',
          beforeState: existingNote,
          afterState: { ...existingNote, tasks: updatedTasks, completionPercentage },
        },
        userId,
        userName,
        true
      );

    } catch (error: any) {
      console.error('Error updating task status:', error);
      throw new Error(`Failed to update task status: ${error.message}`);
    }
  }

  /**
   * Add a source citation to a research note
   */
  async addSourceCitation(
    noteId: string,
    source: Omit<SourceCitation, 'id'>,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      const docRef = doc(db, this.collectionName, noteId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error('Research note not found');
      }

      const existingNote = this.mapDocumentToEntity(docSnap.data(), docSnap.id);

      // Check permissions
      if (!this.checkOwnerOrAdmin(existingNote, userId, userRole)) {
        throw new Error('You do not have permission to add sources to this research note');
      }

      // Create new source
      const newSource: SourceCitation = {
        ...source,
        id: `source_${Date.now()}_${Math.random().toString(36).substring(2)}`,
      };

      const updatedSources = [...existingNote.sources, newSource];

      // Update document
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        sources: updatedSources,
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log change
      await ChangeTrackingService.logChange(
        {
          entityType: 'research',
          entityId: noteId,
          changeType: 'update',
          beforeState: existingNote,
          afterState: { ...existingNote, sources: updatedSources },
        },
        userId,
        userName,
        true
      );

    } catch (error: any) {
      console.error('Error adding source citation:', error);
      throw new Error(`Failed to add source citation: ${error.message}`);
    }
  }

  /**
   * Soft delete a research note
   */
  async deleteResearchNote(
    noteId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Use BaseContentService soft delete (handles change tracking and audit logging)
      await this.softDelete(noteId, userId, userName, userRole);
    } catch (error: any) {
      console.error('Error deleting research note:', error);
      throw new Error(`Failed to delete research note: ${error.message}`);
    }
  }

  /**
   * Approve a pending shared research note (admin only)
   */
  async approveResearchNote(
    noteId: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Get note to check if it's private
      const entity = await this.getById(noteId);
      if (!entity) {
        throw new Error('Research note not found');
      }

      if (entity.isPrivate) {
        throw new Error('Private research notes do not require approval');
      }

      // Use BaseContentService approve method
      await this.approve(noteId, userId, userName, 'admin');

      // Update approval status field (ResearchNote-specific)
      const docRef = doc(db, this.collectionName, noteId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'approved',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error approving research note:', error);
      throw new Error(`Failed to approve research note: ${error.message}`);
    }
  }

  /**
   * Reject a pending shared research note (admin only)
   */
  async rejectResearchNote(
    noteId: string,
    reason: string,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      // Get note to check if it's private
      const entity = await this.getById(noteId);
      if (!entity) {
        throw new Error('Research note not found');
      }

      if (entity.isPrivate) {
        throw new Error('Private research notes do not require approval');
      }

      // Use BaseContentService reject method
      await this.reject(noteId, reason, userId, userName, 'admin');

      // Update approval status field (ResearchNote-specific)
      const docRef = doc(db, this.collectionName, noteId);
      const { updateDoc } = await import('firebase/firestore');
      await updateDoc(docRef, {
        approvalStatus: 'rejected',
        updatedAt: serverTimestamp(),
      });

    } catch (error: any) {
      console.error('Error rejecting research note:', error);
      throw new Error(`Failed to reject research note: ${error.message}`);
    }
  }

  /**
   * Get all shared research notes awaiting approval (admin only)
   */
  async getResearchNotesAwaitingApproval(limit?: number): Promise<ResearchNote[]> {
    try {
      // Get unapproved notes using base method
      const entities = await this.getAwaitingApproval(limit || 50);

      // Filter out private notes (private notes don't need approval)
      const sharedEntities = entities.filter(entity => !entity.isPrivate);

      return sharedEntities as unknown as ResearchNote[];
    } catch (error: any) {
      console.error('Error getting pending research notes:', error);
      throw new Error(`Failed to get pending research notes: ${error.message}`);
    }
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Singleton instance of ResearchNoteService
 */
export const ResearchNoteService = new ResearchNoteServiceImpl();
